import { useState } from 'react';
import { Briefcase, Users, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: '',
    message: '',
  });

  const positions = [
    {
      title: 'Car Wash Technician',
      type: 'Full-time',
      description: 'Join our team of professionals delivering premium car wash services at customer doorsteps.',
      requirements: [
        'Experience in car washing/detailing preferred',
        'Good communication skills',
        'Physical fitness for outdoor work',
        'Valid two-wheeler license',
      ],
    },
    {
      title: 'Field Executive',
      type: 'Full-time',
      description: 'Coordinate with customers and manage service schedules in your assigned area.',
      requirements: [
        'Customer service experience',
        'Good organizational skills',
        'Smartphone proficiency',
        'Local area knowledge',
      ],
    },
  ];

  const benefits = [
    'Competitive salary',
    'Performance bonuses',
    'Training & skill development',
    'Growth opportunities',
    'Flexible schedules',
    'Supportive team environment',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateWhatsAppMessage = () => {
    return encodeURIComponent(
      `Hi Washora, I'm interested in joining your team.\n\n` +
      `👤 Name: ${formData.name || 'Not provided'}\n` +
      `📞 Phone: ${formData.phone || 'Not provided'}\n` +
      `📧 Email: ${formData.email || 'Not provided'}\n` +
      `💼 Position: ${formData.position || 'Not specified'}\n` +
      `📝 Message: ${formData.message || 'Not provided'}`
    );
  };

  const handleSubmit = () => {
    const message = generateWhatsAppMessage();
    window.open(`https://wa.me/919044499957?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20" data-testid="careers-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            <Briefcase className="h-4 w-4 mr-2" />
            Join Our Team
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Careers at Washora
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Be part of Karnataka's first premium doorstep car wash brand. We're looking for passionate individuals who want to make a difference.
          </p>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
                Why Join Washora?
              </h2>
              <p className="text-[#64748B] mb-8 leading-relaxed">
                At Washora, we're building more than just a car wash company – we're creating a new standard for vehicle care in Karnataka. Join us and be part of our exciting journey.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-[#F7931E] mr-3 flex-shrink-0" />
                    <span className="text-[#0B2C5D]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-3xl p-12 text-white">
              <Users className="h-16 w-16 mb-6 text-[#F7931E]" />
              <h3 className="font-heading text-2xl font-bold mb-4">
                Growing Team
              </h3>
              <p className="text-white/80">
                Join our expanding team and grow with Karnataka's premium car care brand. We value dedication, quality, and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Open Positions
            </h2>
            <p className="text-[#64748B]">Current opportunities to join our team.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {positions.map((position, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all"
                data-testid={`position-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold text-[#0B2C5D]">
                    {position.title}
                  </h3>
                  <span className="bg-[#1E88E5]/10 text-[#1E88E5] px-3 py-1 rounded-full text-sm font-medium">
                    {position.type}
                  </span>
                </div>
                <p className="text-[#64748B] mb-6">{position.description}</p>
                <div className="space-y-2">
                  <p className="font-medium text-[#0B2C5D] mb-2">Requirements:</p>
                  {position.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-start text-sm text-[#64748B]">
                      <div className="w-1.5 h-1.5 bg-[#F7931E] rounded-full mr-2 mt-2"></div>
                      {req}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center text-sm text-[#64748B]">
                    <MapPin className="h-4 w-4 mr-1 text-[#1E88E5]" />
                    Chitradurga
                  </div>
                  <div className="flex items-center text-sm text-[#64748B]">
                    <Clock className="h-4 w-4 mr-1 text-[#1E88E5]" />
                    7AM - 7PM
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Apply Now
            </h2>
            <p className="text-[#64748B]">Fill in your details and we'll get back to you.</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-[#0B2C5D] font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="mt-2 bg-slate-50 border-slate-200"
                    data-testid="careers-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#0B2C5D] font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Your phone number"
                    className="mt-2 bg-slate-50 border-slate-200"
                    data-testid="careers-phone"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-[#0B2C5D] font-medium">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Your email"
                    className="mt-2 bg-slate-50 border-slate-200"
                    data-testid="careers-email"
                  />
                </div>
                <div>
                  <Label htmlFor="position" className="text-[#0B2C5D] font-medium">
                    Position Interested In
                  </Label>
                  <Input
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    placeholder="e.g., Car Wash Technician"
                    className="mt-2 bg-slate-50 border-slate-200"
                    data-testid="careers-position"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#0B2C5D] font-medium">
                  Tell Us About Yourself
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your experience, skills, and why you want to join Washora..."
                  rows={4}
                  className="mt-2 bg-slate-50 border-slate-200"
                  data-testid="careers-message"
                />
              </div>

              <Button
                onClick={handleSubmit}
                className="w-full bg-[#F7931E] hover:bg-[#E08215] text-white py-6 text-lg rounded-full font-semibold transition-all transform hover:-translate-y-0.5 shadow-lg"
                data-testid="careers-submit"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Application via WhatsApp
              </Button>

              <p className="text-center text-sm text-[#64748B]">
                Or email your resume to{' '}
                <a href="mailto:washorapremiumcare@gmail.com" className="text-[#1E88E5] hover:underline">
                  washorapremiumcare@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
